import { mlvLogoImgHtml } from '../../../core/brand/mlvLogo.js';
import { authErrorMessageEs } from '../domain/authErrorMessageEs.js';
import { withBusyButton } from '../../../core/ui/withBusyButton.js';
import { mlvTrack } from '../../../core/telemetry/mlvTrack.js';
import { MLV_EVENTS } from '../../../core/telemetry/mlvEvents.js';

const googleIconSvg = `
  <svg class="h-5 w-5 shrink-0" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
  </svg>
`;

export function createAuthPresenter({
  render,
  authUseCases,
  onGuestAccess,
  onLoginSuccess,
}) {
  function loginView() {
    render(`
      <main class="min-h-screen bg-gradient-to-b from-brand-surface via-brand-muted/80 to-emerald-50/60 mlv-bg-surface px-5 py-10 flex flex-col items-center justify-start sm:justify-center">
        <div class="w-full max-w-md">
          <header class="flex flex-col items-center text-center mb-8">
            <div class="mb-4 flex items-center justify-center rounded-2xl bg-white/90 px-4 py-3 shadow-sm ring-1 ring-brand-primary/10">
              ${mlvLogoImgHtml('hero')}
            </div>
            <h1 class="sr-only">MercadoLaVictoria</h1>
            <p class="mt-3 text-sm text-slate-600">Comprá y vendé con gente de tu barrio.</p>
          </header>

          <div id="login-feedback" class="hidden mb-4 rounded-2xl border px-4 py-3 flex gap-3 items-start" role="alert">
            <span id="login-feedback-icon" class="shrink-0 mt-0.5"></span>
            <p id="login-msg" class="text-sm leading-snug"></p>
          </div>

          <button type="button" id="btn-google" class="btn-async group flex w-full items-center justify-center gap-3 rounded-2xl bg-white py-3.5 px-4 text-base font-semibold text-slate-800 shadow-md ring-1 ring-slate-200/80 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:ring-offset-brand-surface">
            ${googleIconSvg}
            <span>Continuar con Google</span>
          </button>

          <p class="mt-3 text-center text-xs text-slate-500">Iniciás sesión con tu cuenta de Google. No guardamos contraseñas.</p>

          <div class="relative py-5">
            <div class="absolute inset-0 flex items-center" aria-hidden="true">
              <div class="w-full border-t border-slate-200/90"></div>
            </div>
            <div class="relative flex justify-center text-xs font-medium uppercase tracking-wide text-slate-500">
              <span class="bg-gradient-to-b from-brand-surface via-brand-muted/80 to-emerald-50/60 px-3">o</span>
            </div>
          </div>

          <button type="button" id="btn-guest" class="mlv-btn-secondary w-full rounded-2xl bg-brand-secondary py-3.5 px-4 text-base font-semibold text-emerald-950 shadow-sm transition hover:bg-brand-secondary/90 focus:outline-none focus:ring-2 focus:ring-brand-secondary focus:ring-offset-2 focus:ring-offset-brand-surface">
            Solo mirar (sin cuenta)
          </button>
        </div>
      </main>
    `);

    function setLoginFeedback(kind, text) {
      const wrap = document.getElementById('login-feedback');
      const msgEl = document.getElementById('login-msg');
      const iconEl = document.getElementById('login-feedback-icon');
      if (!wrap || !msgEl || !iconEl) return;
      if (!text) {
        wrap.classList.add('hidden');
        msgEl.textContent = '';
        iconEl.innerHTML = '';
        return;
      }
      wrap.classList.remove('hidden');
      msgEl.textContent = text;
      if (kind === 'success') {
        wrap.className =
          'mb-4 rounded-2xl border px-4 py-3 flex gap-3 items-start border-emerald-200 bg-emerald-50/95 text-emerald-900';
        iconEl.innerHTML = `<svg class="w-5 h-5 text-emerald-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>`;
      } else {
        wrap.className =
          'mb-4 rounded-2xl border px-4 py-3 flex gap-3 items-start border-red-200 bg-red-50/95 text-red-900';
        iconEl.innerHTML = `<svg class="w-5 h-5 text-red-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>`;
      }
    }

    const guestBtn = document.getElementById('btn-guest');
    if (guestBtn) {
      guestBtn.onclick = () => {
        void withBusyButton(guestBtn, async () => {
          mlvTrack(MLV_EVENTS.GUEST_ACCESS);
          onGuestAccess?.();
        });
      };
    }

    const googleBtn = document.getElementById('btn-google');
    if (googleBtn) {
      googleBtn.onclick = () => {
        setLoginFeedback('error', '');
        void withBusyButton(googleBtn, async () => {
          try {
            await authUseCases.loginConGoogle();
            mlvTrack(MLV_EVENTS.LOGIN_GOOGLE_OK);
            try {
              onLoginSuccess?.();
            } catch (_e) {
              /* ignore */
            }
          } catch (err) {
            console.error(err);
            mlvTrack(MLV_EVENTS.LOGIN_GOOGLE_FAIL, { code: String(err?.code || 'unknown') });
            // Si el usuario cierra el popup o lo cancela, no mostramos error ruidoso.
            const code = String(err?.code || '');
            if (
              code === 'auth/popup-closed-by-user' ||
              code === 'auth/cancelled-popup-request'
            ) {
              return;
            }
            setLoginFeedback('error', authErrorMessageEs(err));
          }
        });
      };
    }
  }

  return {
    loginView,
  };
}
